import { useState, useCallback, useRef } from 'react';
import { toast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

export interface UseSaveFlowOptions<TResult = unknown> {
  /** Entity label for default messages (e.g. "event", "place") */
  entity?: string;
  /** Custom success toast message. Default: "Sparat" */
  successMessage?: string;
  /** Custom error toast message. Default: "Kunde inte spara" */
  errorMessage?: string;
  /** Navigate to this path after successful save */
  onSuccessNavigateTo?: string | ((result: TResult) => string);
  /** Callback after successful save (runs before navigation) */
  onSuccess?: (result: TResult) => void;
}

/**
 * Standardised save flow for Farmly editors.
 *
 * Features:
 * - Prevents double-save (ignores calls while isSaving)
 * - Shows success/error toast
 * - Optional post-save navigation
 * - Preserves data on error
 *
 * @example
 * const { save, isSaving } = useSaveFlow(
 *   (data) => updateMutation.mutateAsync(data),
 *   { entity: 'producer', successMessage: 'Profilen sparad' }
 * );
 */
export function useSaveFlow<TArgs = void, TResult = unknown>(
  saveFn: (args: TArgs) => Promise<TResult>,
  options: UseSaveFlowOptions<TResult> = {},
) {
  const {
    successMessage = 'Sparat',
    errorMessage = 'Kunde inte spara',
    onSuccessNavigateTo,
    onSuccess,
  } = options;

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const savingRef = useRef(false);
  const navigate = useNavigate();

  const save = useCallback(
    async (args: TArgs) => {
      // Prevent parallel saves
      if (savingRef.current) return undefined;
      savingRef.current = true;
      setIsSaving(true);
      setError(null);

      try {
        const result = await saveFn(args);

        toast({ title: successMessage });
        onSuccess?.(result);

        if (onSuccessNavigateTo) {
          const path =
            typeof onSuccessNavigateTo === 'function'
              ? onSuccessNavigateTo(result)
              : onSuccessNavigateTo;
          navigate(path);
        }

        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        console.error(`[useSaveFlow] Save failed:`, e);
        toast({
          title: errorMessage,
          description: e.message || 'Försök igen.',
          variant: 'destructive',
        });
        return undefined;
      } finally {
        setIsSaving(false);
        savingRef.current = false;
      }
    },
    [saveFn, successMessage, errorMessage, onSuccessNavigateTo, onSuccess, navigate],
  );

  return { save, isSaving, error } as const;
}
